import { baseUrl, forgerockErrors } from "../utilities/configurations";
import { resetConfig } from "../utilities/helper";
import apiRequest from "../utilities/request";
import { useNavigate } from "react-router";
import { useAuth } from "./AuthContext";
import PropTypes from "prop-types";
import {
  UserManager,
  StepType,
  FRAuth,
} from "@forgerock/javascript-sdk";
import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useMemo,
} from "react";
import { server_error_message, server_error_title } from "../utilities/values";

const MyAccountContext = createContext();

export function useMyAccountContext() {
  return useContext(MyAccountContext);
}

export function MyAccountProvider(props) {
  const initMemberStatus = {
    isMember: false,
    isDependent: false,
    isPrepaid: false,
    isKycVerified: true
  }

  const [memberStatus, setMemberStatus] = useState(initMemberStatus);
  const [displaySignOutModal, setDisplaySignOutModal] = useState(false);
  const [showExpiredOtpModal, setShowExpiredOtpModal] = useState(false);
  const [configRestarted, setConfigRestarted] = useState(false);
  const [displayAlert, setDisplayAlert] = useState(false);
  const [mobileNumber, setMobileNumber] = useState("");
  const [journeyStep, setJourneyStep] = useState(null);
  const [alertMessage, setAlertMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [alertTitle, setAlertTitle] = useState("");
  const [email, setEmail] = useState("");
  const [type, setType] = useState("");

  const { logout, setUserData, setUser } = useAuth();
  const navigate = useNavigate();

  const emailRegex = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,4}$/;
  const isEmailValid = email !== "";
  const isEmailComplete = email !== "" && emailRegex.test(email);
  const isMobileValid = mobileNumber !== "";
  const isMobileComplete =
    mobileNumber !== "" &&
    mobileNumber.startsWith("9") &&
    mobileNumber.length === 10;

  const [otp, setOtp] = useState("");

  useEffect(() => {
    getPolicy();
    getMemberStatus();
  }, []);

  useEffect(() => {
    if (configRestarted) {
      resetConfig();
      setConfigRestarted(false);
    }
  }, [configRestarted]);

  const resetValues = () => {
    setOtp("");
    setEmail("");
    setMobileNumber("");
    setAlertTitle("");
    setAlertMessage("");
    setDisplayAlert(false);
    setShowExpiredOtpModal(false);
  };

  const getPolicy = async () => {
    const res = await apiRequest(baseUrl + `/policy/policies`, "GET");
    if (!res.error) {
      setUserData(res.data || []);
    } else {
      console.log(res.message);
    }
  };

  const getMemberStatus = async () => {
    try {
      const res = await apiRequest(baseUrl + "/member/member-status", "GET");
      if (!res.error) {
        setMemberStatus(res.data || initMemberStatus)
      }
    } catch (error) {
      console.log(error);
    }
  };

  const handleSubmit = async () => {
    setIsLoading(true);
    let journey_obj = JSON.parse(JSON.stringify(journeyStep));
    journey_obj.callbacks[0].payload.input[0].value = otp;
    journey_obj.payload.callbacks[0].input[0].value = otp;
    try {
      const nextStep = await FRAuth.next(journey_obj);
      switch (nextStep.type) {
        case StepType.Step:
          if (nextStep.payload.callbacks[0].type === "PasswordCallback") {
            setAlertMessage("Oops! Incorrect OTP. Please try again");
          } else if (
            nextStep.payload.callbacks[0].type === "TextOutputCallback" &&
            nextStep.payload.callbacks[0].output[0].value === "OTP is Expired"
          ) {
            setShowExpiredOtpModal(true);
          }
          setJourneyStep(nextStep);
          break;
        case StepType.LoginSuccess:
          {
            const fr_user = await UserManager.getCurrentUser();
            if (fr_user) {
              setUser(fr_user);
            }
            navigate("/my-account/update-details/success");
          }
          break;
        case StepType.LoginFailure:
          {
            const detail = nextStep.getDetail();
            const errorReason = nextStep.getReason();
            const errorMessage = nextStep.getMessage();
            if (detail?.errorCode === "110") {
              setDisplayAlert(true);
              setAlertTitle(errorReason);
              setAlertMessage(errorMessage);
            } else {
              setDisplayAlert(true);
              setAlertTitle("Error");
              setAlertMessage(
                forgerockErrors[detail?.failureUrl] || forgerockErrors["SWW"]
              );
            }
            resetValues();
            setConfigRestarted(true);
            navigate("/my-account/update-details", { replace: true });
          }
          break;
      }
    } catch (err) {
      console.log(err);
      setDisplayAlert(true);
      setAlertTitle(server_error_title);
      setAlertMessage(server_error_message);
      resetValues();
      setConfigRestarted(true);
      navigate("/my-account/update-details", { replace: true });
    } finally {
      setIsLoading(false);
    }
  };

  const handleBack = () => {
    navigate(-1);
    setOtp("");
    setAlertTitle("");
    setAlertMessage("");
    setDisplayAlert(false);
  };

  const formatMobileNumber = (e) => {
    const inputVal = e.target.value.replace(/ /g, "");
    let inputNumbersOnly = inputVal.replace(/\D/g, "");

    if (inputNumbersOnly.length > 10) {
      inputNumbersOnly = inputNumbersOnly.substr(0, 10);
    }

    setMobileNumber(inputNumbersOnly);
    setDisplayAlert(false);
    setAlertTitle("");
    setAlertMessage("");
  };

  const handleSignOut = async () => {
    setIsLoading(true);
    logout();
  };

  const data = useMemo(
    () => ({
      setDisplaySignOutModal,
      setShowExpiredOtpModal,
      formatMobileNumber,
      setConfigRestarted,
      setMobileNumber,
      setDisplayAlert,
      setAlertMessage,
      setJourneyStep,
      setAlertTitle,
      handleSignOut,
      handleSubmit,
      setIsLoading,
      setShowModal,
      resetValues,
      handleBack,
      navigate,
      setEmail,
      setType,
      setOtp,
      memberStatus,
      displaySignOutModal,
      showExpiredOtpModal,
      isMobileComplete,
      configRestarted,
      isEmailComplete,
      isMobileValid,
      isEmailValid,
      displayAlert,
      mobileNumber,
      alertMessage,
      journeyStep,
      alertTitle,
      isLoading,
      showModal,
      email,
      type,
      otp,
    }),
    [
      memberStatus,
      displaySignOutModal,
      showExpiredOtpModal,
      isMobileComplete,
      isEmailComplete,
      configRestarted,
      isMobileValid,
      isEmailValid,
      alertMessage,
      mobileNumber,
      displayAlert,
      journeyStep,
      alertTitle,
      isLoading,
      showModal,
      email,
      type,
      otp,
    ]
  );

  return (
    <MyAccountContext.Provider value={data}>
      {props.children}
    </MyAccountContext.Provider>
  );
}

MyAccountProvider.propTypes = {
  children: PropTypes.any,
};
